<template>
  <page layout="tabbed">
    <page-header
      :title="pageTitle"
      :breadcrumbs="breadcrumbs"
      :tags="headerTags"
      :meta="headerMeta"
    >
      <template #actions>
        <el-button
          type="primary"
          plain
          @click="runNow"
          data-qa="run-now-button"
        >
          <i class="el-icon-refresh-right"></i>
          {{ $t('Automations_RunNow') }}
        </el-button>
        <el-button
          v-if="isDelta"
          type="warning"
          plain
          @click="resetDelta"
          data-qa="reset-delta-button"
        >
          <i class="el-icon-refresh"></i>
          {{ $t('Automations_ResetDelta') }}
        </el-button>
      </template>
    </page-header>

    <page-tabs :tabs="tabs"></page-tabs>
  </page>
</template>

<script>
import Page from '@/common/components/page/Page'
import PageHeader from '@/common/components/page/PageHeaderNew'
import PageTabs from '@/common/components/page/PageTabs'
import { projectEndpoint, projectRoute } from '@/core/helpers'
import { DayOfWeekLabel } from '@/modules/automations/enum/RecurrenceEnum'
import RestApi from '@/core/services/RestApi'

export default {
  name: 'ImportRunHistoryPage',

  components: {
    Page,
    PageHeader,
    PageTabs
  },

  provide() {
    return {
      automationData: () => this.automation
    }
  },

  data() {
    return {
      automation: null,
      integrationName: ''
    }
  },

  mounted() {
    this.fetchAutomation()
  },

  computed: {
    automationId() {
      return this.$route.query.id
    },

    automationName() {
      return this.automation ? this.automation.name : ''
    },

    isDelta() {
      return (
        this.automation &&
        this.automation.source &&
        this.automation.source.sync_mode === 'delta'
      )
    },

    headerTags() {
      if (!this.automation) return []

      const tags = []

      if (!this.automation.active) {
        tags.push({
          type: 'danger',
          effect: 'plain',
          label: this.$t('Automations_Field_StatusInactive')
        })
      }

      return tags
    },

    headerMeta() {
      if (!this.automation) return null

      const meta = []

      if (this.integrationName) {
        meta.push({
          icon: 'el-icon-connection',
          label: `${this.$t('Automations_RunHistory_Source')}: ${
            this.integrationName
          }`
        })
      }

      if (this.automation.active && this.automation.schedule) {
        meta.push({
          icon: 'el-icon-time',
          label: `${this.$t(
            'Automations_RunHistory_NextSyncTime'
          )}: ${this.formatNextSyncTime()}`
        })
      }

      if (this.isDelta) {
        meta.push({
          icon: 'el-icon-s-grid',
          label: `${this.$t('Imports_Field_DeltaColumn')}: ${
            this.automation.source.delta_column
          }`
        })
        meta.push({
          icon: 'el-icon-data-line',
          label: `${this.$t('Automations_RunHistory_CurrentDeltaValue')}: ${this
            .automation.source.delta_value || 'N/A'}`
        })
      }

      return meta.length ? meta : null
    },

    pageTitle() {
      const title = this.$t('Automations_RunHistory_Title')
      return this.automationName ? `${title}: ${this.automationName}` : title
    },

    tabs() {
      return [
        {
          title: this.$t('Automations_RunHistory_Tab_Syncs'),
          route: projectRoute({
            name: 'AutomationImportRunHistorySyncs',
            query: { id: this.automationId }
          })
        },
        {
          title: this.$t('Automations_RunHistory_Tab_ImportTasks'),
          route: projectRoute({
            name: 'AutomationImportRunHistoryTasks',
            query: { id: this.automationId }
          })
        }
      ]
    },

    breadcrumbs() {
      return [
        {
          label: this.$t('Automations_Breadcrumb_Data')
        },
        {
          label: this.$t('Automations_Breadcrumb_Automations')
        },
        {
          label: this.$t('Automations_Breadcrumb_Imports'),
          route: projectRoute({ name: 'AutomationImports' })
        },
        {
          label: this.automationName || '...',
          route: projectRoute({
            name: 'AutomationImportUpdate',
            query: { id: this.automationId }
          })
        },
        {
          label: this.$t('Automations_RunHistory_Title')
        }
      ]
    }
  },

  methods: {
    fetchAutomation() {
      RestApi(
        projectEndpoint({
          method: 'get',
          url: `import-automations/${this.automationId}`
        })
      ).then(({ data }) => {
        this.automation = data
        this.fetchIntegration()
      })
    },

    fetchIntegration() {
      const integrationId =
        this.automation &&
        this.automation.source &&
        this.automation.source.integration_id
      if (!integrationId) return

      RestApi(
        projectEndpoint({
          method: 'get',
          url: `integrations/${integrationId}`
        })
      ).then(({ data }) => {
        this.integrationName = data.name
      })
    },

    runNow() {
      RestApi(
        projectEndpoint({
          method: 'post',
          url: `import-automations/${this.automationId}/run`
        })
      )
        .then(({ data }) => {
          this.$router.push(
            projectRoute({
              name: 'TaskHistoryView',
              query: { id: data.id }
            })
          )
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },

    formatNextSyncTime() {
      const schedule = this.automation.schedule
      if (!schedule) return 'N/A'

      switch (schedule.frequency) {
        case 'hourly':
          return this.$t('Automations_RunHistory_Schedule_Hourly', {
            minutes: schedule.minutes || '00'
          })
        case 'daily':
          return this.$t('Automations_RunHistory_Schedule_Daily', {
            time: schedule.time
          })
        case 'weekly': {
          const days = (schedule.day || [])
            .map(d => this.$t(DayOfWeekLabel[d]))
            .join(', ')
          return this.$t('Automations_RunHistory_Schedule_Weekly', {
            days,
            time: schedule.time
          })
        }
        case 'monthly':
          return this.$t('Automations_RunHistory_Schedule_Monthly', {
            day: schedule.day,
            time: schedule.time
          })
        default:
          return 'N/A'
      }
    },

    resetDelta() {
      this.$confirm(
        this.$t('Automations_ResetDelta_Confirm'),
        this.$t('Automations_ResetDelta'),
        { type: 'warning' }
      )
        .then(() => {
          RestApi(
            projectEndpoint({
              method: 'post',
              url: `import-automations/${this.automationId}/reset-delta`
            })
          )
            .then(() => {
              this.automation.source.delta_value = null
              this.$message({
                message: this.$t('Automations_ResetDelta_Success'),
                type: 'success'
              })
            })
            .catch(error => {
              this.$message({
                message: error.message,
                type: 'error'
              })
            })
        })
        .catch(() => {})
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/modules/automations/pages/ImportRunHistoryPage.vue